import {
  adminGetOperatorRunQuery,
  AdminOperatorRunDetail
} from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import DownloadIcon from "@mui/icons-material/Download"
import LinkIcon from "@mui/icons-material/Link"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import {
  buildDebugBundle,
  copyDebugBundle,
  downloadDebugBundle
} from "@utils/debug_bundle"
import { showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"
import { useMemo, useState } from "react"

import JsonBox from "./JsonBox"
import OperatorChatThread from "./OperatorChatThread"
import OperatorStepRow from "./OperatorStepRow"
import RunStatus from "./RunStatus"
import {
  firstFailure,
  formatMs,
  formatUsd,
  waterfall
} from "./operatorRun.utils"

type TabKey = "steps" | "chat" | "raw"

function Meta({ items }: { items: [string, string | undefined][] }) {
  return (
    <Stack direction="row" flexWrap="wrap" columnGap={1} rowGap={0.25}>
      {items
        .filter(([, value]) => value)
        .map(([label, value], index) => (
          <Tooltip key={label} title={label}>
            <Typography variant="caption" color="text.secondary">
              {index ? `· ${value}` : value}
            </Typography>
          </Tooltip>
        ))}
    </Stack>
  )
}

function Body({
  detail,
  onClose
}: {
  detail: AdminOperatorRunDetail
  onClose: () => void
}) {
  const { translate } = useTranslate()
  const { run, steps } = detail
  const failure = firstFailure(detail)
  const [tab, setTab] = useState<TabKey>("steps")
  const [open, setOpen] = useState<number | undefined>(failure?.step?.order)
  const bars = useMemo(
    () => waterfall(steps, run.durationMs),
    [steps, run.durationMs]
  )
  const bundle = () =>
    buildDebugBundle({ kind: "operator-run", id: run.id, data: detail })

  const copy = (text: string) =>
    void navigator.clipboard
      .writeText(text)
      .then(() => showSuccessToast(translate("ai_operator_copied")))

  return (
    <Stack gap={2}>
      <Stack gap={0.75}>
        <Stack direction="row" gap={1} alignItems="flex-start">
          <Typography
            variant="subtitle1"
            fontWeight={600}
            whiteSpace="pre-wrap"
            flex={1}
            lineHeight={1.4}
          >
            {run.request}
          </Typography>
          <Tooltip title={translate("ai_operator_copy_link")}>
            <IconButton
              size="small"
              onClick={() =>
                copy(`${window.location.origin}/ai/operator/${run.id}`)
              }
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate("ai_operator_copy_bundle")}>
            <IconButton
              size="small"
              onClick={() => void copyDebugBundle(bundle())}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate("ai_operator_download_bundle")}>
            <IconButton
              size="small"
              onClick={() => downloadDebugBundle(bundle())}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <RunStatus run={run} />
          <Tooltip title={translate("ai_operator_decision")}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontFamily="monospace"
            >
              {[run.kind, run.decision].filter(Boolean).join(" · ")}
            </Typography>
          </Tooltip>
        </Stack>
        <Meta
          items={[
            [
              translate("ai_operator_time"),
              dayjs(run.createdAt).format("DD.MM.YYYY HH:mm:ss")
            ],
            [
              translate("ai_operator_customer"),
              run.customerName ?? run.customerId
            ],
            [translate("ai_operator_user"), run.userName ?? run.cPersonId],
            [translate("ai_operator_duration"), formatMs(run.durationMs)],
            [translate("ai_operator_cost"), formatUsd(run.costUsd)],
            [
              translate("ai_operator_tokens"),
              run.usage
                ? `${run.usage.inputTokens}→${run.usage.outputTokens} token`
                : undefined
            ],
            [
              translate("ai_operator_engine"),
              run.engine
                ? `${run.engine.version} (${run.engine.source})`
                : undefined
            ],
            [translate("ai_operator_screen"), run.context?.screen]
          ]}
        />
      </Stack>

      {failure ? (
        <Alert
          severity="error"
          action={
            failure.step ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setTab("steps")
                  setOpen(failure.step?.order)
                }}
              >
                {translate("ai_operator_show_step")}
              </Button>
            ) : undefined
          }
        >
          <Typography variant="body2" whiteSpace="pre-wrap">
            <b>{`${run.error?.code ?? translate("ai_operator_status_failed")} · ${failure.stage ?? ""}`}</b>
            {failure.message ? ` — ${failure.message}` : ""}
          </Typography>
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_event, value: TabKey) => setTab(value)}
        sx={{ minHeight: 36, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          sx={{ minHeight: 36, textTransform: "none" }}
          value="steps"
          label={`${translate("ai_operator_tab_steps")} (${steps.length})`}
        />
        <Tab
          sx={{ minHeight: 36, textTransform: "none" }}
          value="chat"
          label={`${translate("ai_operator_tab_chat")} (${detail.chat?.messages.length ?? 0})`}
        />
        <Tab
          sx={{ minHeight: 36, textTransform: "none" }}
          value="raw"
          label={translate("ai_operator_tab_raw")}
        />
      </Tabs>

      {tab === "steps" ? (
        steps.length ? (
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            {steps.map((step, index) => (
              <Box
                key={step.order}
                sx={{ borderTop: index ? 1 : 0, borderColor: "divider" }}
              >
                <OperatorStepRow
                  step={step}
                  bar={bars[index]}
                  expanded={open === step.order}
                  onToggle={() =>
                    setOpen(open === step.order ? undefined : step.order)
                  }
                />
              </Box>
            ))}
          </Paper>
        ) : (
          <Alert severity="info">{translate("ai_operator_no_steps")}</Alert>
        )
      ) : null}

      {tab === "chat" ? (
        <OperatorChatThread messages={detail.chat?.messages ?? []} />
      ) : null}

      {tab === "raw" ? (
        <Stack gap={2}>
          {run.error?.stack ? (
            <JsonBox
              label={translate("ai_operator_stack")}
              value={run.error.stack}
            />
          ) : null}
          <JsonBox
            label={translate("ai_operator_run_record")}
            value={run}
            maxHeight={600}
          />
        </Stack>
      ) : null}
    </Stack>
  )
}

export default function OperatorRunDetail({
  runId,
  onClose
}: {
  runId: string
  onClose: () => void
}) {
  const { translate } = useTranslate()
  const detail = useQuery({
    queryKey: ["ai-operator-run", runId],
    queryFn: async () => {
      const response = await adminGetOperatorRunQuery(runId)
      if (!response.isSuccess || !response.data) {
        throw new Error(
          response.errorMessage || translate("ai_operator_not_found")
        )
      }
      return response.data
    },
    retry: false
  })

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      {detail.isLoading ? (
        <Stack alignItems="center" p={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : null}
      {detail.error ? (
        <Alert
          severity="error"
          action={
            <IconButton size="small" color="inherit" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {detail.error.message}
        </Alert>
      ) : null}
      {detail.data ? <Body detail={detail.data} onClose={onClose} /> : null}
    </Box>
  )
}
